
import Vue from "vue";
import TabNavigation from "@/components/molecules/TabNavigation.vue";
import FishCounter from "@/components/atoms/FishCounter.vue";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import AssistRequestContent from "@/components/molecules/AssistRequestContent.vue";
import ManuscriptSelectContent from "@/components/molecules/ManuscriptSelectContent.vue";
import HistoryListContent from "@/components/molecules/HistoryListContent.vue";
import ResultViewContent from "@/components/molecules/ResultViewContent.vue";
import { AssistContentType } from "@/store/modules/assistNavigation";
import { logError, logWarn } from "@/lib/utils/logger";
import { AssistAnalytics } from "@/lib/utils/analytics/assist";

interface AssistPanelData {
  analytics: AssistAnalytics | null;
  currentRequestType: "feedback" | "matching" | "catchcopy" | null;
}

interface AssistPanelMethods {
  handleTabChange(tabId: string): void;
  handleSuggestionClick(suggestion: any): void;
  handleConfirmSelection(manuscript: any): void;
  executeNavigationFlow(manuscript: any): Promise<void>;
  handleManuscriptSelect(manuscript: any): void;
  handleManuscriptsLoaded(manuscripts: any[]): void;
  handleManuscriptsError(error: any): void;
  handleBackToRequest(): void;
  dismissErrors(): void;
  clearAllErrors(): void;
  getResultFeedbackText(): string;
  handleRouteChange(newRoute: any, oldRoute?: any): Promise<void>;
  extractNovelIdFromRoute(route: any): string | null;
  initializePanelState(): void;
  cleanupPanelState(): void;
}

interface AssistPanelComputed {
  activeTab: string;
  currentContent: AssistContentType;
  error: string | null;
  selectedItemId: string | null;
  isApiLoading: boolean;
  apiError: string | null;
  hasAnyError: boolean;
  isAnyLoading: boolean;
  isVisible: boolean;
  currentContentComponent: string;
  currentContentProps: Record<string, any>;
}

export default Vue.extend<AssistPanelData, AssistPanelMethods, AssistPanelComputed, {}>({
  name: "AssistPanel",
  components: {
    TabNavigation,
    FishCounter,
    ChatMessage,
    AssistRequestContent,
    ManuscriptSelectContent,
    HistoryListContent,
    ResultViewContent,
  },
  data(): AssistPanelData {
    return {
      analytics: null,
      currentRequestType: null,
    };
  },
  computed: {
    // 🔧 Phase 5 修正: activeTabをVuexから取得
    activeTab(): string {
      return this.$store.getters["assistNavigationModule/currentTab"] || "assist";
    },

    // 画面遷移関連のgetters
    currentContent(): AssistContentType {
      return this.$store.getters["assistNavigationModule/currentContent"];
    },
    error(): string | null {
      return this.$store.getters["assistNavigationModule/error"];
    },
    selectedItemId(): string | null {
      return this.$store.getters["assistNavigationModule/selectedItemId"];
    },

    // 原稿選択関連のgetters
    isApiLoading(): boolean {
      return this.$store.getters["assistSelectManuscriptModule/isApiLoading"];
    },
    apiError(): string | null {
      return this.$store.getters["assistSelectManuscriptModule/apiError"];
    },

    // 複合的な状態
    hasAnyError(): boolean {
      return !!(this.error || this.apiError);
    },
    isAnyLoading(): boolean {
      return this.isApiLoading;
    },

    // パネル表示状態
    isVisible(): boolean {
      return this.$store.getters["assistNavigationModule/isVisible"];
    },

    currentContentComponent(): string {
      const componentMap: Record<AssistContentType, string> = {
        request: "AssistRequestContent",
        "manuscript-select": "ManuscriptSelectContent",
        result: "ResultViewContent",
        history: "HistoryListContent",
      };
      return componentMap[this.currentContent] || "AssistRequestContent";
    },

    // 🆕 Phase 5: パフォーマンス最適化 - 計算キャッシュ対応
    currentContentProps(): Record<string, any> {
      const baseProps: Record<string, any> = {};
      const contentType = this.currentContent;

      // 🔧 条件分岐を最適化し、不要な計算を削減
      switch (contentType) {
        case "manuscript-select": {
          const novelId = this.$store.getters["assistSelectManuscriptModule/selectedNovelId"];
          return {
            ...baseProps,
            novelId, // Vuexから取得、デフォルトはnull
            useApi: true, // API機能を有効化
            requestType: this.currentRequestType, // リクエスト種別を渡す
          };
        }

        case "result": {
          // 結果表示画面用のprops
          return {
            ...baseProps,
            feedbackText: this.getResultFeedbackText(),
          };
        }

        case "history": {
          return {
            ...baseProps,
            // HistoryListContentに必要なpropsがあれば追加
          };
        }

        case "request":
        default: {
          return baseProps;
        }
      }
    },
  },
  watch: {
    activeTab: {
      immediate: true,
      handler(newTab: string) {
        // 🔧 修正: result表示中はタブ変更による自動リセットを抑制
        if (this.currentContent === "result" && newTab === "assist") {
          return;
        }

        if (newTab === "assist") {
          // アシストタブがアクティブになった時はコンテンツを依頼画面に戻すのみ
          // パネルが表示中は原稿選択状態をリセットしない
          this.$store.dispatch("assistNavigationModule/showRequestContent");
          // assistSelectManuscriptModule の状態は維持する（非表示時のみクリア）
        }
      },
    },

    // 🔧 URL監視機能（Phase 2設計）- Phase 5 で最適化
    $route: {
      immediate: true,
      handler(newRoute: any, oldRoute?: any) {
        // 🔧 修正: result表示中はURL変更による自動リセットを抑制
        if (this.currentContent === "result") {
          return;
        }

        // アシストパネルが表示されている時のみデータ取得を実行
        if (this.isVisible) {
          this.handleRouteChange(newRoute, oldRoute);
        }
      },
    },

    // 🆕 パネル表示状態の監視（Phase 5 追加）
    isVisible: {
      handler(newVisible: boolean, oldVisible?: boolean) {
        if (newVisible && !oldVisible) {
          // パネル表示時のアナリティクス
          if (this.analytics) {
            this.analytics.trackPanelOpen();
          }
          this.initializePanelState();
        } else if (!newVisible && oldVisible) {
          // パネル非表示時のアナリティクス
          if (this.analytics) {
            this.analytics.trackPanelClose();
          }
          this.cleanupPanelState();
        }
      },
    },
  },
  methods: {
    handleTabChange(tabId: string): void {
      if (tabId === "close") {
        this.$store.dispatch("assistNavigationModule/hidePanel");
        this.$store.dispatch("assistSelectManuscriptModule/resetState");
        this.$emit("close");
        return;
      }

      // 1. Vuexのタブ状態を更新
      this.$store.dispatch("assistNavigationModule/setCurrentTab", tabId);

      // 2. タブ別のコンテンツ処理
      if (tabId === "assist") {
        this.$store.dispatch("assistNavigationModule/showRequestContent");
      } else if (tabId === "history") {
        this.$store.dispatch("assistNavigationModule/showHistory");
      }

      // 3. 親コンポーネントに通知
      this.$emit("tab-change", tabId);
    },

    handleSuggestionClick(suggestion: any): void {
      // リクエスト種別を保存
      if (suggestion.id === "feedback" || suggestion.action === "request-feedback") {
        this.currentRequestType = "feedback";
      } else if (suggestion.id === "matching" || suggestion.action === "request-matching") {
        this.currentRequestType = "matching";
      } else if (suggestion.id === "catchcopy" || suggestion.action === "request-catchcopy") {
        this.currentRequestType = "catchcopy";
      }

      // 原稿選択画面に遷移
      this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
    },

    handleConfirmSelection(manuscript: any): void {
      // 🆕 Phase 5: ナビゲーションフローの改善
      this.executeNavigationFlow(manuscript);
    },

    // 🆕 統合ナビゲーションフローの実行（Phase 5 追加）
    async executeNavigationFlow(manuscript: any): Promise<void> {
      try {
        // 1. エラー状態をクリア
        this.clearAllErrors();

        // 2. 原稿情報を保存（必要に応じて）
        if (manuscript?.id) {
          await this.$store.dispatch("assistNavigationModule/showResultWithId", { id: manuscript.id });
        } else {
          await this.$store.dispatch("assistNavigationModule/showResult");
        }
      } catch (error) {
        logError({ module: "AssistPanel", action: "executeNavigationFlow" }, "フロー実行エラー", error);
        await this.$store.dispatch("assistNavigationModule/setError", "結果表示に失敗しました");
      }
    },

    handleManuscriptSelect(manuscript: any): void {
      this.$store.dispatch("assistSelectManuscriptModule/selectManuscript", manuscript);
    },

    handleManuscriptsLoaded(manuscripts: any[]): void {
      // Phase 3で削除されたアクションの代わりに、適切な処理を実装
    },

    handleManuscriptsError(error: any): void {
      logError({ module: "AssistPanel", action: "handleManuscriptsError" }, "原稿一覧の読み込みでエラーが発生", error);
      // Phase 3で削除されたアクションの代わりに、エラー表示処理を実装
      logError({ module: "AssistPanel", action: "handleManuscriptsError" }, "原稿一覧取得エラー", error);
    },

    handleBackToRequest(): void {
      this.$store.dispatch("assistNavigationModule/showRequestContent");
    },

    dismissErrors(): void {
      // 🆕 Phase 5: 統合エラーハンドリング
      this.clearAllErrors();
    },

    // 🆕 全てのエラー状態をクリアする統合メソッド（Phase 5 追加）
    clearAllErrors(): void {
      // 画面遷移関連のエラーをクリア（新しいアクションを追加）
      this.$store.dispatch("assistNavigationModule/clearError");
      // 原稿選択関連のエラーをクリア（新しいアクションを追加）
      this.$store.dispatch("assistSelectManuscriptModule/clearApiError");
    },

    getResultFeedbackText(): string {
      // selectedItemIdに基づいて適切な感想文を返す
      if (this.selectedItemId) {
        // assistHistoryから履歴データを取得
        const historyList = this.$store.getters["assistHistory/filteredHistoryList"];
        const selectedItem = historyList.find((item: any) => item.id === this.selectedItemId);

        if (selectedItem) {
          return (
            selectedItem.fullFeedback || selectedItem.feedbackPreview || "フィードバック内容が見つかりませんでした。"
          );
        }
        logWarn(
          { module: "AssistPanel", action: "getResultFeedbackText" },
          "指定されたIDの履歴アイテムが見つかりません",
          { selectedItemId: this.selectedItemId }
        );
        return `選択されたアイテム（AssistID: ${this.selectedItemId}）のデータが見つかりませんでした。\n\n履歴データが読み込まれていない可能性があります。過去ログタブから再度選択してください。`;
      }

      logWarn(
        { module: "AssistPanel", action: "getResultFeedbackText" },
        "指定されたIDの履歴アイテムが見つかりません",
        { selectedItemId: this.selectedItemId }
      );
      return `選択されたアイテム（AssistID: ${this.selectedItemId}）のデータが見つかりませんでした。\n\n履歴データが読み込まれていない可能性があります。過去ログタブから再度選択してください。`;
    },

    // 🆕 URL変化対応メソッド（Phase 2設計）
    async handleRouteChange(newRoute: any, oldRoute?: any) {
      try {
        // URLからnovelIdを抽出
        const novelId = this.extractNovelIdFromRoute(newRoute);

        if (novelId) {
          // Novel IDが存在する場合：データ取得＆UI表示
          await this.$store.dispatch("assistSelectManuscriptModule/initializeWithNovelId", novelId);
        } else {
          // Novel IDが存在しない場合：エラー状態表示
          await this.$store.dispatch("assistSelectManuscriptModule/showNoManuscriptError");
        }
      } catch (error) {
        logError({ module: "AssistPanel", action: "handleRouteChange" }, "Route変化処理でエラー", error);
        await this.$store.dispatch("assistSelectManuscriptModule/showNoManuscriptError");
      }
    },

    // 🆕 RouteからNovel IDを抽出するヘルパーメソッド
    extractNovelIdFromRoute(route: any): string | null {
      // 1. パラメータから抽出（例: /novels/:novelId/manuscripts）
      if (route.params?.novelId) {
        return route.params.novelId;
      }

      // 2. クエリパラメータから抽出（例: /manuscripts?novelId=123）
      if (route.query?.novelId) {
        return route.query.novelId;
      }

      // 3. パスから正規表現で抽出（実際のアプリ構造に対応）
      // Nolaアプリの実際の構造: /:novelId/editor, /:novelId/detail など
      const pathMatch = route.path.match(/^\/([^/]+)\/(editor|detail|characters|worldviews|materials|plot|timeline)/);
      if (pathMatch && pathMatch[1]) {
        return pathMatch[1];
      }

      // 4. Novel IDが見つからない場合
      return null;
    },

    // 🆕 パネル表示状態の初期化メソッド（Phase 5 追加） - 緊急修正版
    initializePanelState() {
      // 🔧 緊急修正: 重複処理を避ける（created()で既に実行済み）

      // URL変更検知を手動トリガー（必要に応じて）
      if (this.$route) {
        this.handleRouteChange(this.$route);
      }
    },

    // 🆕 パネル表示状態のクリーンアップメソッド（Phase 5 追加）
    cleanupPanelState() {
      // パネル非表示時の状態クリーンアップ処理を実装

      // 原稿選択状態をクリア（パネル非表示時のみ）
      this.$store.dispatch("assistSelectManuscriptModule/resetState");

      // 作家診断結果をクリア
      this.$store.dispatch("writerTypeResultModule/clearResult");

      // 必要に応じて他のクリーンアップ処理を追加
    },
  },

  // 🆕 Phase 5: パネル表示制御の最適化 - 緊急修正版
  // コンポーネント初期化時は基本的な状態管理を実行
  created() {
    // Analytics初期化
    this.analytics = new AssistAnalytics(this.$gtm);

    // 🔧 修正: 結果表示中でない場合のみ初期化を実行
    if (this.currentContent !== "result") {
      // Vuex状態の初期化（ナビゲーション状態のみ、原稿選択状態は維持）
      this.$store.dispatch("assistNavigationModule/resetState");
      // assistSelectManuscriptModule の状態は維持する（パネル表示中は保持）
    }

    // パネルが既に表示状態の場合は追加の初期化を実行
    if (this.isVisible) {
      this.initializePanelState();
    }
  },

  beforeDestroy() {
    // アシスト履歴のポーリング停止
    this.$store.dispatch("assistHistory/stopPolling");

    // アシスト関連モジュールの状態リセット
    this.$store.dispatch("assistNavigationModule/resetState");
    this.$store.dispatch("assistSelectManuscriptModule/resetState");
    this.$store.dispatch("assistHistory/resetState");
    this.$store.dispatch("writerTypeResultModule/clearResult");
  },
});
